/**
 * PH-C4 — Production_Hardening_Prompts C4.
 *
 * Dedup hook for the SED-amendment fetch. Before this pack, three
 * surfaces fired the same `fetchSEDAmendments(sedId)` call independently
 * from their own bare `useEffect`s with `[sed.id]` deps:
 *
 *   1. `SiteLandingPage` (Site Evidence tab) — line ~3027 — fired on
 *      every site change via `[site.sedId, fetchSEDAmendments]`.
 *   2. `SEDVariableBrowser` — line 84 — bare effect with `[sed.id]` dep
 *      (the LLM-deps-miss flagged by the pack — `fetchSEDAmendments`
 *      omitted from the dep array, would silently capture a stale
 *      reference if it ever changed identity).
 *   3. `SEDSectionEditor` — line 119 — identical bare-effect pattern.
 *
 * When the Site Evidence tab opened, SiteLandingPage's effect AND the
 * child SEDVariableBrowser's effect both fired against the same sedId,
 * doubling Firestore round-trips and re-triggering the store's
 * `isLoadingAmendments` flag mid-render. This hook collapses all three
 * call sites onto one fetch per `(sedId, session)`:
 *
 *   - In-flight dedup: a module-level `Map<sedId, Promise<void>>` —
 *     concurrent mounts that ask for the same `sedId` share the same
 *     pending promise instead of firing a second Firestore query.
 *   - Loaded-state skip: when the store already shows the requested
 *     sedId as the `activeSedId` AND a load isn't in flight AND
 *     amendments are present, the hook short-circuits without
 *     touching Firestore at all.
 *   - Refresh propagation: when `sedId` changes, the hook honours the
 *     new id and fires a fresh fetch (the in-flight map keys on sedId,
 *     so the prior id's load doesn't leak).
 *
 * The hook deliberately returns nothing — the data lives in the
 * Zustand `amendmentState` slice, and consumers subscribe to whichever
 * slice they care about (Browser reads `amendments`; SignOff reads
 * `revisions`; etc.). This keeps the dedupe orthogonal to consumer
 * subscription patterns.
 */
import { useEffect } from 'react';
import { useStore } from '../../store';

/**
 * Module-level in-flight registry. Keyed by sedId so concurrent loads
 * of *different* SEDs proceed in parallel; concurrent loads of the
 * *same* SED resolve to the same promise.
 *
 * This deliberately persists for the lifetime of the JS module — it
 * survives unmount/remount cycles of consumer components (which is the
 * point: a Strict-Mode double-mount must not double-fetch). When the
 * promise resolves, the entry is cleared so a later explicit refresh
 * (e.g. after an amendment is applied and the SED bumps version) still
 * triggers a real Firestore round-trip.
 */
const inFlight = new Map<string, Promise<void>>();

/**
 * Public escape hatch for tests + post-mutation refresh paths. Clears
 * the in-flight registry so the next `useSedAmendments` mount fires a
 * real fetch regardless of prior dedup state. Not part of the normal
 * runtime contract — callers should let the hook manage dedup on its
 * own.
 */
export function __resetSedAmendmentsDedupForTests(): void {
  inFlight.clear();
}

/**
 * Subscribe to the SED-amendments slice for `sedId`, deduping concurrent
 * mounts and skipping the fetch entirely when the data is already
 * loaded for the same id.
 *
 * Safe to call from N components with the same `sedId` — only the
 * first wins; the rest piggy-back on the in-flight promise or the
 * already-loaded store state.
 *
 * Passing `undefined` or empty string is a no-op (mirrors the prior
 * bare-effect guard `if (sed.id) fetchSEDAmendments(sed.id)`).
 */
export function useSedAmendments(sedId: string | null | undefined): void {
  // Zustand action references are stable for the store's lifetime, so
  // including this in the dep array is safe and matches the
  // exhaustive-deps lint rule.
  const fetchSEDAmendments = useStore(s => s.fetchSEDAmendments);

  useEffect(() => {
    if (!sedId) return;

    // Skip-if-loaded: the store already holds the amendments for this
    // sedId, no load is in flight, and the slice has been populated
    // (activeSedId is the marker `fetchSEDAmendments` sets at the start
    // of every load). This is the fast path for the "child mounts after
    // parent has already finished loading" case (the dominant
    // SEDVariableBrowser path).
    const state = useStore.getState().amendmentState;
    const alreadyLoaded =
      state.activeSedId === sedId && !state.isLoadingAmendments;
    if (alreadyLoaded) return;

    // Dedup-in-flight: a concurrent mount already started this fetch;
    // attach to its promise instead of firing again. This is the
    // critical path for the SiteLandingPage + SEDVariableBrowser
    // simultaneous-mount case.
    if (inFlight.has(sedId)) return;

    // Cold path: fire the fetch, register the promise so concurrent
    // mounts within the same tick can dedupe, and clear the registry
    // entry when it settles (success or failure — a stuck "in flight"
    // entry would make future refreshes silently no-op).
    const promise = fetchSEDAmendments(sedId)
      .catch(() => undefined)
      .finally(() => {
        // Only clear if we're still the registered entry — defensive
        // against rapid sedId churn where a second mount could have
        // overwritten the slot (shouldn't happen with the `has` guard
        // above, but cheap to verify).
        if (inFlight.get(sedId) === promise) inFlight.delete(sedId);
      });
    inFlight.set(sedId, promise);
  }, [sedId, fetchSEDAmendments]);
}
